import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { LocalizationService } from '../../core/localization/localization.service';
import {
  GrowthAttributionModel,
  GrowthAttributionRun,
  GrowthAttributionTouchpoint,
  GrowthIntelligenceService
} from './growth-intelligence.service';
import { buildGrowthMarketingChannelOptions, growthPublishChannelLabel } from './growth-channel.constants';
import { combineUtcDateAndTime, defaultCalendarFromUtc, defaultCalendarToUtc } from './growth-datetime.util';

interface AttributionRuleRow {
  channelKey: string;
  weightPercent: number;
}

@Component({
  standalone: true,
  selector: 'ain-growth-attribution',
  imports: [
    AinPageComponent, AinStateComponent, AinFormFieldComponent, InfoHintComponent, JalaliDateInputComponent,
    FormsModule, TableModule, ButtonModule, SelectModule, InputTextModule, InputNumberModule, DialogModule,
    AinDatePipe
  ],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.intelligence.attribution.title')" [description]="t('growth.intelligence.attribution.description')">
      <ain-info-hint page-title-addon [titleKey]="'growth.intelligence.attribution.help.title'" [descriptionKey]="'growth.intelligence.attribution.help.description'"
        [bulletKeys]="['growth.intelligence.attribution.help.bullets.rules', 'growth.intelligence.attribution.help.bullets.ufc']" />

      <section class="card">
        <div class="toolbar">
          <button pButton type="button" [label]="t('growth.intelligence.attribution.actions.newModel')" (click)="openModelDialog()"></button>
          <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="loadModels()"></button>
        </div>
        @if (loadingModels()) {
          <ain-state type="loading" [title]="t('common.loading')" />
        } @else if (models().length === 0) {
          <ain-state type="empty" [title]="t('common.noRecords')" />
        } @else {
          <p-table [value]="models()" [rows]="8" [paginator]="true">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.intelligence.attribution.columns.name') }}</th>
                <th>{{ t('growth.intelligence.attribution.columns.type') }}</th>
                <th>{{ t('growth.intelligence.attribution.columns.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.name }}</td>
                <td>{{ row.modelType }}</td>
                <td>{{ row.status }}</td>
              </tr>
            </ng-template>
          </p-table>
        }
      </section>

      <section class="card">
        <div class="section-title"><h3>{{ t('growth.intelligence.attribution.sections.run') }}</h3></div>
        <div class="grid">
          <ain-form-field [label]="t('growth.intelligence.attribution.fields.model')">
            <p-select [options]="modelOptions()" optionLabel="label" optionValue="value" [(ngModel)]="runForm.attributionModelId" appendTo="body" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.intelligence.attribution.fields.periodStart')"><ain-date-input [(ngModel)]="runForm.periodStartUtc" /></ain-form-field>
          <ain-form-field [label]="t('growth.intelligence.attribution.fields.periodEnd')"><ain-date-input [(ngModel)]="runForm.periodEndUtc" /></ain-form-field>
          <ain-form-field [label]="t('growth.intelligence.attribution.fields.revenueSourceMode')">
            <p-select [options]="revenueModeOptions" optionLabel="label" optionValue="value" [(ngModel)]="runForm.revenueSourceMode" appendTo="body" />
          </ain-form-field>
        </div>
        <button pButton type="button" [label]="t('growth.intelligence.attribution.actions.run')" (click)="runAttribution()"></button>
        @if (lastRun()) {
          <p><strong>{{ t('growth.intelligence.attribution.fields.totalRevenue') }}:</strong> {{ lastRun()!.totalAttributedRevenue }}</p>
        }
      </section>

      <section class="card">
        <div class="section-title"><h3>{{ t('growth.intelligence.attribution.sections.touchpoints') }}</h3></div>
        @if (touchpoints().length === 0) {
          <ain-state type="empty" [title]="t('growth.intelligence.attribution.emptyTouchpoints')" />
        } @else {
          <p-table [value]="touchpoints()" [rows]="10" [paginator]="true">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.intelligence.attribution.columns.channel') }}</th>
                <th>{{ t('growth.intelligence.attribution.columns.touchType') }}</th>
                <th>{{ t('growth.intelligence.attribution.columns.occurredAt') }}</th>
                <th>{{ t('growth.intelligence.attribution.columns.weight') }}</th>
                <th>{{ t('growth.intelligence.attribution.columns.revenue') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ channelLabel(row.channelKey) }}</td>
                <td>{{ row.touchType }}</td>
                <td>{{ row.occurredAtUtc | ainDate:'datetime' }}</td>
                <td>{{ row.weight }}</td>
                <td>{{ row.revenueAmount ?? '-' }}</td>
              </tr>
            </ng-template>
          </p-table>
        }
      </section>

      <p-dialog
        [header]="t('growth.intelligence.attribution.actions.newModel')"
        [visible]="modelDialogVisible()"
        (visibleChange)="modelDialogVisible.set($event)"
        [modal]="true"
        [style]="{ width: 'min(760px, 96vw)' }">
        <ain-info-hint
          [titleKey]="'growth.intelligence.attribution.help.title'"
          [descriptionKey]="'growth.intelligence.attribution.help.description'"
          [bulletKeys]="['growth.intelligence.attribution.help.bullets.rules']" />
        <div class="grid">
          <ain-form-field [label]="t('growth.intelligence.attribution.fields.name')"><input pInputText [(ngModel)]="modelForm.name" /></ain-form-field>
          <ain-form-field [label]="t('growth.intelligence.attribution.fields.modelType')">
            <p-select [options]="modelTypeOptions" optionLabel="label" optionValue="value" [(ngModel)]="modelForm.modelType" appendTo="body" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.intelligence.attribution.fields.windowDays')"><p-inputNumber [(ngModel)]="modelForm.windowDays" [min]="1" [max]="365" /></ain-form-field>
        </div>
        <div class="section-title"><h4>{{ t('growth.intelligence.attribution.sections.ruleRows') }}</h4></div>
        @for (row of ruleRows; track $index) {
          <div class="row-grid">
            <ain-form-field [label]="t('growth.intelligence.attribution.fields.channel')">
              <p-select [options]="channelOptions()" optionLabel="label" optionValue="value" [(ngModel)]="row.channelKey" appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.intelligence.attribution.fields.weightPercent')"><p-inputNumber [(ngModel)]="row.weightPercent" [min]="0" [max]="100" /></ain-form-field>
            <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="removeRuleRow($index)"></button>
          </div>
        }
        <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="addRuleRow()"></button>
        <ng-template pTemplate="footer">
          <div class="dialog-footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="modelDialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('common.save')" (click)="saveModel()"></button>
          </div>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 8px; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .section-title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .row-grid { display: grid; gap: 8px; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); align-items: end; margin-bottom: 8px; }
    .dialog-footer { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }
  `]
})
export class GrowthAttributionComponent implements OnInit {
  private readonly service = inject(GrowthIntelligenceService);
  private readonly i18n = inject(LocalizationService);

  readonly loadingModels = signal(false);
  readonly modelDialogVisible = signal(false);
  readonly models = signal<GrowthAttributionModel[]>([]);
  readonly lastRun = signal<GrowthAttributionRun | null>(null);
  readonly touchpoints = signal<GrowthAttributionTouchpoint[]>([]);

  modelForm = { name: '', modelType: 'Linear', windowDays: 30, status: 'Active' };
  ruleRows: AttributionRuleRow[] = [{ channelKey: 'email', weightPercent: 50 }];
  runForm = {
    attributionModelId: null as string | null,
    periodStartUtc: defaultCalendarFromUtc(),
    periodEndUtc: defaultCalendarToUtc(),
    revenueSourceMode: 'sandbox'
  };

  readonly modelTypeOptions = [
    { value: 'FirstTouch', label: 'First touch' },
    { value: 'LastTouch', label: 'Last touch' },
    { value: 'Linear', label: 'Linear' },
    { value: 'TimeDecay', label: 'Time decay' }
  ];
  readonly revenueModeOptions = [
    { value: 'sandbox', label: 'Sandbox' },
    { value: 'boundary', label: 'UFC boundary' }
  ];

  ngOnInit(): void { this.loadModels(); }

  channelOptions(): Array<{ label: string; value: string }> {
    return buildGrowthMarketingChannelOptions(key => this.t(key));
  }

  channelLabel(channelKey: string): string {
    return growthPublishChannelLabel(channelKey, key => this.t(key));
  }

  modelOptions() {
    return this.models().map(item => ({ value: item.id, label: item.name }));
  }

  loadModels(): void {
    this.loadingModels.set(true);
    this.service.listAttributionModels().subscribe({
      next: items => {
        this.models.set(items);
        if (!this.runForm.attributionModelId && items.length > 0)
          this.runForm.attributionModelId = items[0].id;
        this.loadingModels.set(false);
      },
      error: () => this.loadingModels.set(false)
    });
  }

  openModelDialog(): void {
    this.modelForm = { name: '', modelType: 'Linear', windowDays: 30, status: 'Active' };
    this.ruleRows = [{ channelKey: 'email', weightPercent: 50 }];
    this.modelDialogVisible.set(true);
  }

  saveModel(): void {
    this.service.createAttributionModel({
      name: this.modelForm.name,
      modelType: this.modelForm.modelType,
      windowDays: this.modelForm.windowDays,
      rulesJson: JSON.stringify(this.ruleRows),
      status: this.modelForm.status
    }).subscribe({
      next: () => {
        this.modelDialogVisible.set(false);
        this.loadModels();
      }
    });
  }

  runAttribution(): void {
    if (!this.runForm.attributionModelId) return;
    this.service.runAttribution({
      attributionModelId: this.runForm.attributionModelId,
      periodStartUtc: combineUtcDateAndTime(this.runForm.periodStartUtc, '00:00')!,
      periodEndUtc: combineUtcDateAndTime(this.runForm.periodEndUtc, '23:59')!,
      revenueSourceMode: this.runForm.revenueSourceMode,
      idempotencyKey: `attribution-run-${this.runForm.attributionModelId}-${Date.now()}`
    }).subscribe({
      next: result => {
        this.lastRun.set(result.run);
        this.service.listTouchpoints({ runId: result.run.id }).subscribe({ next: rows => this.touchpoints.set(rows) });
      }
    });
  }

  addRuleRow(): void { this.ruleRows.push({ channelKey: 'web', weightPercent: 0 }); }
  removeRuleRow(index: number): void { this.ruleRows.splice(index, 1); }
  t(key: string): string { return this.i18n.translate(key); }
}
